import type { paths } from "@/api-schema";
import { alphartisApiAxios } from "@/config/axios";
import { useQuery } from "@tanstack/react-query";
import { handlePaginatedParameters } from "../_helpers/handle-parameters";
import { optionsWithDefaultsPaginated } from "../_helpers/options-with-defaults";
import { PaginatedOptions, UseQueryOptionsNoKeyAndFn } from "../_types";

export const MODEL_FAMILY_SEARCH_RESOURCE = "model-family" as const;
export const MODEL_FAMILY_SEARCH_OPERATION = "search" as const;
export const MODEL_FAMILY_SEARCH_API_PATH =
  `/v1/${MODEL_FAMILY_SEARCH_RESOURCE}/${MODEL_FAMILY_SEARCH_OPERATION}` as const;

export type ModelFamilySearchParameters = paths[typeof MODEL_FAMILY_SEARCH_API_PATH]["post"]["parameters"]["query"];
export type ModelFamilySearchBody =
  paths[typeof MODEL_FAMILY_SEARCH_API_PATH]["post"]["requestBody"]["content"]["application/json"];
export type ModelFamilySearchResponse =
  paths[typeof MODEL_FAMILY_SEARCH_API_PATH]["post"]["responses"][200]["content"]["application/json"];

export function useModelFamilySearchQuery({
  options,
  enabled,
  queryOptions,
}: {
  options?: PaginatedOptions<ModelFamilySearchParameters, ModelFamilySearchBody>;
  enabled?: boolean;
  queryOptions?: UseQueryOptionsNoKeyAndFn<
    ModelFamilySearchResponse,
    Error,
    ModelFamilySearchResponse,
    readonly unknown[]
  >;
} = {}) {
  const optionsWithDefaults =
    optionsWithDefaultsPaginated<PaginatedOptions<ModelFamilySearchParameters, ModelFamilySearchBody>>(options);

  const requestParameters = {
    ...(optionsWithDefaults.parameters || {}),
    ...handlePaginatedParameters(optionsWithDefaults.parameters),
  };

  return useQuery({
    queryKey: [
      MODEL_FAMILY_SEARCH_RESOURCE,
      MODEL_FAMILY_SEARCH_OPERATION,
      { parameters: requestParameters, body: optionsWithDefaults.body },
    ],
    queryFn: async () => {
      const response = await alphartisApiAxios.post<ModelFamilySearchResponse>(
        MODEL_FAMILY_SEARCH_API_PATH,
        optionsWithDefaults.body,
        {
          params: requestParameters,
        },
      );

      return response.data;
    },
    ...queryOptions,
    enabled,
  });
}
